import { HelpCircle, X } from 'lucide-react';
import LoaderOverlay from './LoaderOverlay';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'info' | 'warning';
  loading?: boolean;
  loadingText?: string;
}

const ConfirmationModal = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'info',
  loading = false,
  loadingText = 'Processing...',
}: ConfirmationModalProps) => {
  if (!isOpen) return null;

  const getConfirmButtonStyles = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning':
        return 'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-400';
      default:
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  const getIconStyles = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-100 text-red-600';
      case 'warning':
        return 'bg-yellow-100 text-yellow-600';
      default:
        return 'bg-blue-100 text-blue-600';
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      <LoaderOverlay isLoading={loading} loadingText={loadingText}>
        <div className="relative z-10 w-full max-w-md mx-4 transform rounded-2xl bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 p-4 sm:p-6 shadow-2xl transition-all">
          <button
            onClick={onCancel}
            disabled={loading}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${getIconStyles()}`}
          >
            <HelpCircle className="h-8 w-8" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {title}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
              {message}
            </p>
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-3">
            <button
              type="button"
              disabled={loading}
              className={`w-full rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${getConfirmButtonStyles()}`}
              onClick={onConfirm}
            >
              {confirmText}
            </button>
            <button
              type="button"
              disabled={loading}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onCancel}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </LoaderOverlay>
    </div>
  );
};

export default ConfirmationModal;
